import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'antd';

import arrowBlue from 'assets/images/arrow-blue.png';
import calendarPng from 'assets/images/calendar.png';
import ModalEndSubmit from 'components/Organisms/Modals/ModalEndSubmit/ModalEndSubmit';
import UploadDocs from 'components/Atoms/UploadDocs/UploadDocs';
import { LoanContext } from 'scenes/Private/Dashboard/Dashboard';
import { File, Loan, TaskState } from 'gql/graphql';
import { useCreateResponseTask, useDeleteFileResponse } from 'services/task/mutations';
import { useGetTaskResponsesByTask } from 'services/task/querys';
import ModalFinish from '../ModalFinish';
import SectionErrorTask from 'scenes/Private/components/SectionErrorTask/SectionErrorTask';
import SectionSuccessTask from 'scenes/Private/components/SectionSuccessTask/SectionSuccessTask';
import CollapseIssues from 'scenes/Private/components/CollapseIssues/CollapseIssues';
import StateTag from 'components/Atoms/Tags/StateTag/StateTag';
import IssuesTaskResponse from 'scenes/Private/components/IssuesTaskResponse/IssuesTaskResponse';

interface Props {
	task: any;
	setModalTaskVisible: any;
	redirectTrackRecord: any;
	generateCircles: any;
	countProperties?: number;
	response: any;
	closeTask: any;
}

const Step1 = ({
	task,
	setModalTaskVisible,
	redirectTrackRecord,
	generateCircles,
	countProperties,
	response,
	closeTask
}: Props) => {
	const loan: Loan | any = useContext(LoanContext);

	const { execute: _deleteFile } = useDeleteFileResponse();
	const { fetching, execute: createResponseTask } = useCreateResponseTask();
	const { reexecute: getTaskResponses } =
		useGetTaskResponsesByTask({
			variables: { findResponsesByTask: { taskId: task._id } }
		});

	const [modalFinish, setModalFinish] = useState<boolean>(false);
	const [isModalConfirm, setIsModalConfirm] = useState(false);
	const [listFileUpload, setListFileUpload] = useState([]);
	const [taskCompleted, setTaskCompleted] = useState<boolean>();

	useEffect(() => {
		setTaskCompleted(response?.state === TaskState.Approved)
	}, [response])

	const submit = async () => {
		let payload: any = {
			key: 'property-huds',
			label: 'Property HUDs',
			step: 1,
			metadata: {},
			state: TaskState.Review,
			task: task?._id,
			files: listFileUpload.filter((item: any) => !!item.size)
		};

		if (response) payload._id = response._id;

		await createResponseTask(payload);
		setIsModalConfirm(false);
		getTaskResponses({ requestPolicy: 'network-only' });

		if ((response?.files?.length === 1 && listFileUpload.length === 1) || listFileUpload.length === 2)
			setModalFinish(true);

		setListFileUpload([]);

	};

	const mixedFiles = (includeError?: boolean) => {
		let files = response?.files.filter((file: any) => file.state !== 'request_error' || includeError) || [];
		const mixed = [...files, ...listFileUpload];

		const include: any = {};

		return mixed.filter((item) => {
			if (!include[item.name]) {
				include[item.name] = true;
				return true;
			}
			return false;
		});
	};

	const onDeleteFile = (file: File) => {
		if(response) _deleteFile({ fileKey: file.key, taskResponseId: response._id });	
	}
	
	const reviewFiles = response?.files?.filter((item: any) => item.state === 'review') || [];
	const countPropertiesNotReview = (countProperties || 0) - reviewFiles.length;
	const filesIgnoreError = response?.files?.filter((item: any) => item.state !== 'request_error');
	

	return (
		<div className="children InvestmentExperienceVerification">
			<h2 className={`h2 ${response?.state === TaskState.Review ? 'mt-20 h2-inline' : ''}`}>
				Upload the HUDs for at least {loan.investmentExperience.properties}{' '}
				properties
			</h2>
			<p className="h4 h4-w4 mt-5">
				Don’t remember which properties we’re referring to?{' '}
				<span className="blue-bold" onClick={redirectTrackRecord}>
					View the list of properties you used to verify your experience
					<img src={arrowBlue} className="arrow" alt="arrow" />
				</span>
			</p>

			{taskCompleted &&
				<SectionSuccessTask
					title="You've completed this task."
					button={{ title: "Back to dashboard", onClick: closeTask }}
				/>
			}

			{!taskCompleted &&
				<SectionErrorTask taskId={task._id} titleAddText={`Issues with`} isTitleDocument/>
			}

			<div
				className={`card__generic card__generic-${response?.state} mt-50`}
				style={{ position: 'relative' }}
			>
				<StateTag state={response?.state} isLabelLocked />

				<div id={response?._id} className="flex-btw mt-10">
					<p className="h4">Property HUDs</p>
					<span>Submitted HUDs</span>
				</div>
				{countProperties && (
					<UploadDocs
						listFileUpload={mixedFiles() || listFileUpload}
						responsesFiles={mixedFiles(true) || listFileUpload}
						setListFileUpload={setListFileUpload}
						numberOfFiles={countProperties}
						onDeleteFile={onDeleteFile}
						circles={{
							label: `Past properties: ${countProperties?.toString()}`,
							states: generateCircles()
						}}
						button={
							<ModalEndSubmit
								visible={isModalConfirm}
								setVisible={setIsModalConfirm}
								buttonAction={{
                  label: 
										countProperties === 2 ? 
											(listFileUpload.length === 1? 'Submit for review': 'Add at least one HUD to submit') : 
											(listFileUpload.length === 3? 'Submit for review': 'Submit at least 3 HUDs'),
                  disabled:
                    response?.state === TaskState.Approved ||
                    listFileUpload.length <= 0 ||
                    (response?.state === TaskState.RequestError ?
                      (
                        (countProperties === 2 && listFileUpload.length < 1) ||
                        (countProperties !== 2 && listFileUpload.length < 3)
                      )
                      :
                      response?.state !== TaskState.Review && (
                        (countProperties === 2 && listFileUpload.length < 1) ||
                        (countProperties !== 2 && listFileUpload.length < 3) ||
                        filesIgnoreError?.length === countProperties
                      )
                    )
                }}
								modal={{
									title: 'Ready to submit?',
									description: (
										<h4 className="h4 h4-w4">
											{`After submitting, you'll have to contact us to make any changes.`}
										</h4>
									)
								}}
								className="modal-end-submit--white"
								icon={calendarPng}
								buttonSubmit={
									<Button
										className="button button-modal-ok"
										loading={fetching}
										onClick={submit}
									>
										Yes, this is correct
									</Button>
								}
								buttonCancel={(actionCancel: any) => (
									<Button
										className="button button-modal-cancel"
										onClick={actionCancel}
									>
										I want to make changes
									</Button>
								)}
							/>
						}
						insertHtmlBottom={
							response?.state === TaskState.RequestError && <IssuesTaskResponse taskId={task._id} taskResponseId={response._id}/>
						}
					/>
				)}
			</div>

			{/*<AddAnotherDoc taskId={task._id} taskState={task.state}/>*/}

			        <CollapseIssues taskId={task._id} taskKey={task.key} rejectionCode='no_action_required' />

			{modalFinish &&
				<ModalFinish
					visible={modalFinish}
					setVisible={setModalFinish}
					closeTask={() => setModalTaskVisible(false)}
				/>
			}
		</div>
	);
};
export default Step1;
